import axios from "axios";
import {
  User,
  Organization,
  LoginRequest,
  LoginResponse,
  SystemInitRequest,
  CreateOrganizationResponse,
  GetOrganizationsResponse,
  TimeEntryRequest,
  TimeStatusResponse,
  CreateUserRequest,
  CreateUserResponse,
  UpdateUserRequest,
  ResetPasswordResponse,
  OrganizationWithStats,
  UpdateOrganizationRequest,
  DeleteOrganizationResponse,
  StatusEntryRequest,
  TimeEntry,
  StatusEntry,
  DashboardData,
  CalendarData,
  GetUsersResponse,
  GetUserResponse,
  WeekStatusRequest,
  WeekStatusResponse,
  TimeEntriesRequest,
  TimeEntriesResponse,
  UpdateOrganizationSettingsRequest,
  Screenshot,
  BillingEntry,
  CreateBillingEntryRequest,
  UpdateBillingEntryRequest,
  BillingTaskCode,
  BillingOrgName,
  OrgBranch,
  AnalyticsDashboardData,
  AnalyticsAIInsights,
  OrganizationRequest,
  OrgRequestStatus,
  RegisterOrganizationRequestPayload,
} from "../types/api";

import { captureException as sentryCaptureException } from "../lib/sentry";

const API_BASE_URL = import.meta.env.VITE_API_URL || "/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60 seconds for AI analysis requests
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Send cookies with requests
});

// Request interceptor: attach Bearer token and log
api.interceptors.request.use(
  (config) => {
    // Attach JWT from localStorage (needed for cross-origin requests where cookies may not be sent)
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const fullUrl = config.baseURL
      ? `${config.baseURL.replace(/\/$/, "")}/${config.url?.replace(/^\//, "")}`
      : config.url;
    console.log(
      `API Request: ${config.method?.toUpperCase()} ${fullUrl}`,
      config.url === "/time/clock-out" ? "(clock-out)" : ""
    );
    return config;
  },
  (error) => {
    console.error("API Request Error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // A 401 is an expected auth-state signal (no/expired session) that this
    // interceptor already handles by redirecting to login — it isn't an error
    // worth a red console line, and on the public landing page the /auth/me
    // session check returns 401 by design. Log everything else as before.
    if (error.response?.status === 401) {
      // Handle authentication errors
      // Just redirect to login, cookies are cleared by backend or browser
      const publicPaths = ["/login", "/privacy-policy", "/desktop-auth", "/getting-started", "/register-org", "/forgot-password", "/reset-password"];
      const isPublicPath = window.location.pathname === "/" || publicPaths.some(path => window.location.pathname.startsWith(path));
      if (!isPublicPath) {
        window.location.href = "/login";
      }
    } else {
      console.error("API Response Error:", error);
    }

    // Telemetry: forward 5xx + network/abort failures to Sentry. 4xx are
    // business rejections (handled by callers) and would only add noise.
    const status: number | undefined = error?.response?.status;
    const isServerError = typeof status === "number" && status >= 500;
    const isNetworkFailure = !error?.response && error?.code !== "ERR_CANCELED";
    if (isServerError || isNetworkFailure) {
      try {
        const wrapped =
          error instanceof Error
            ? error
            : new Error(error?.message || "API request failed");
        sentryCaptureException(wrapped, {
          endpoint: error?.config?.url,
          method: error?.config?.method,
          status: status ?? null,
          code: error?.code ?? null,
        });
      } catch {
        // capture wrapper already swallows; nothing else to do
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post("/auth/login", data);
    return response.data;
  },

  initializeSystem: async (data: SystemInitRequest): Promise<LoginResponse> => {
    const response = await api.post("/auth/setup/initialize", data);
    return response.data;
  },

  createOrganization: async (
    name: string
  ): Promise<CreateOrganizationResponse> => {
    const response = await api.post("/auth/organizations", { name });
    return response.data;
  },

  getOrganizations: async (): Promise<GetOrganizationsResponse> => {
    const response = await api.get("/auth/organizations");
    return response.data;
  },

  getSystemStatus: async (): Promise<{ isInitialized: boolean }> => {
    const response = await api.get("/auth/setup/status");
    return response.data;
  },

  me: async (): Promise<LoginResponse> => {
    const response = await api.get("/auth/me");
    return response.data;
  },

  logout: async (): Promise<void> => {
    await api.post("/auth/logout");
  },

  getDesktopAuthCode: async (callbackId?: string): Promise<{ success: boolean; code: string }> => {
    const response = await api.post("/auth/desktop-auth-code", callbackId ? { callbackId } : {});
    return response.data;
  },

  // Polled by the web /desktop-auth page to detect whether the desktop app
  // actually picked up the auth code (i.e. whether the simpletime:// protocol
  // handler fired and exchange-code was called). Returns `exchanged: true`
  // once the desktop app has consumed the code.
  pollDesktopCallback: async (callbackId: string): Promise<{ success: boolean; exchanged?: boolean; pending?: boolean }> => {
    const response = await api.get(`/auth/desktop-callback/${callbackId}`);
    return response.data;
  },

  registerOrganization: async (
    payload: RegisterOrganizationRequestPayload
  ): Promise<{ success: boolean; requestId?: string; error?: string; field?: string; errors?: Array<{ msg: string; path?: string }> }> => {
    const response = await api.post("/auth/register-org", payload);
    return response.data;
  },

  // --- Self-service password reset ---
  forgotPassword: async (
    username: string
  ): Promise<{ success: boolean; message: string; expiresInMinutes: number }> => {
    const response = await api.post("/auth/forgot-password", { username });
    return response.data;
  },

  verifyPasswordResetToken: async (
    token: string
  ): Promise<{ success: boolean; username?: string; name?: string; error?: string; code?: string }> => {
    const response = await api.post("/auth/verify-reset-token", { token });
    return response.data;
  },

  submitPasswordReset: async (
    token: string,
    newPassword: string
  ): Promise<{ success: boolean; message?: string; error?: string; code?: string }> => {
    const response = await api.post("/auth/reset-password", { token, newPassword });
    return response.data;
  },
};

// Structured error thrown by clockIn() when the server returns 409 ALREADY_CLOCKED_IN
// (user has an open session on another device). Callers can `instanceof` check this
// to surface the confirmation modal instead of a generic error string.
export class AlreadyClockedInError extends Error {
  openSession: {
    id: string;
    startedAt: string;
    workType: string;
    location: string | null;
    workLocation: string | null;
  };
  constructor(
    message: string,
    openSession: AlreadyClockedInError["openSession"]
  ) {
    super(message);
    this.name = "AlreadyClockedInError";
    this.openSession = openSession;
  }
}

// Time tracking API
export const timeAPI = {
  clockIn: async (data: TimeEntryRequest): Promise<TimeEntry> => {
    try {
      const response = await api.post("/time/clock-in", {
        type: "clock-in",
        notes: data.notes,
        workType: data.workType,
        expectedOvertimeHours: data.expectedOvertimeHours,
        location: data.location,
        workLocation: data.workLocation,
      });
      return response.data.timeEntry || response.data;
    } catch (err: any) {
      // Surface 409 ALREADY_CLOCKED_IN as a typed error so the UI can prompt the
      // user to close the other device's session before retrying.
      const data = err?.response?.data;
      if (err?.response?.status === 409 && data?.error === "ALREADY_CLOCKED_IN") {
        throw new AlreadyClockedInError(data.message || "Already clocked in", data.openSession);
      }
      throw err;
    }
  },

  // Closes the user's open session on another device, marking it as auto-logout
  // with reason NEW_DEVICE_CLOCK_IN. The server leaves dailySummary blank so the
  // recovery-summary flow prompts the user later. Frontend should call clockIn()
  // again after this resolves.
  forceClockoutOtherSessions: async (): Promise<{
    success: boolean;
    closedSessionId: string;
    effectiveClockOut: string;
    needsRecoverySummary: boolean;
  }> => {
    const response = await api.post("/time/force-clockout-other-sessions");
    return response.data;
  },

  clockOut: async (data: TimeEntryRequest): Promise<TimeEntry> => {
    const response = await api.post("/time/clock-out", {
      type: "clock-out",
      notes: data.notes,
      dailySummary: data.dailySummary,
    });
    return response.data.timeEntry;
  },

  getEntries: async (
    userId: string,
    organizationId: string,
    date?: string
  ): Promise<TimeEntry[]> => {
    const params = new URLSearchParams({ userId, organizationId });
    if (date) params.append("date", date);

    const response = await api.get(`/time/entries?${params}`);
    return response.data.entries;
  },

  getStatus: async (
    userId?: string,
    organizationId?: string
  ): Promise<TimeStatusResponse> => {
    const response = await api.post("/time/status");
    return response.data.status;
  },

  /** Check if Electron app is connected for this session (HTTP poll; works in Chrome/Edge) */
  getElectronStatus: async (
    sessionId: string
  ): Promise<{ connected: boolean }> => {
    const response = await api.get(
      `/time/electron-status?sessionId=${encodeURIComponent(sessionId)}`
    );
    return response.data;
  },

  validateSummary: async (summary: string): Promise<{ success: boolean; isMeaningful: boolean; message: string }> => {
    // Longer timeout for AI analysis requests
    const response = await api.post("/time/validate-summary", { summary }, { timeout: 30000 });
    return response.data;
  },

  submitRecoverySummary: async (summary: string): Promise<{ success: boolean }> => {
    const response = await api.patch("/time/recovery-summary", { summary }, { timeout: 15000 });
    return response.data;
  },

  /**
   * Backfill location on a clock-in entry. Fire-and-forget on the client —
   * location is captured asynchronously after a successful clock-in.
   */
  updateLocation: async (
    entryId: string,
    location: string
  ): Promise<{ success: boolean; updated: boolean }> => {
    const response = await api.patch(`/time/entries/${entryId}/location`, {
      location,
    });
    return response.data;
  },
};

// Status API
export const statusAPI = {
  create: async (data: StatusEntryRequest): Promise<StatusEntry> => {
    const response = await api.post("/status/create", data);
    return response.data.statusEntry;
  },

  update: async (
    id: string,
    updates: Partial<StatusEntry>
  ): Promise<StatusEntry> => {
    const response = await api.put(`/status/${id}`, updates);
    return response.data.statusEntry;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/status/${id}`);
  },

  getEntries: async (
    userId: string,
    organizationId: string,
    date?: string
  ): Promise<StatusEntry[]> => {
    const params = new URLSearchParams({ userId, organizationId });
    if (date) params.append("date", date);

    const response = await api.get(`/status/entries?${params}`);
    return response.data.entries;
  },
};

// Dashboard API (admin only)
export const dashboardAPI = {
  getOverview: async (
    _userId: string,
    organizationId: string
  ): Promise<DashboardData> => {
    const response = await api.post("/dashboard/overview", {
      organizationId,
    });
    return response.data.dashboard;
  },

  getCalendar: async (
    userId: string,
    organizationId: string,
    year: number,
    month: number
  ): Promise<CalendarData> => {
    const response = await api.get(
      `/dashboard/calendar?userId=${userId}&organizationId=${organizationId}&year=${year}&month=${month}`
    );
    return response.data.calendar;
  },

  getUsers: async (userId: string, organizationId: string): Promise<User[]> => {
    const response = await api.post("/dashboard/users", {
      userId,
      organizationId,
    });
    return response.data.users;
  },

  getWeekStatus: async (
    data: WeekStatusRequest
  ): Promise<WeekStatusResponse> => {
    const response = await api.post("/dashboard/week-status", data);
    return response.data;
  },

  getTimeEntries: async (
    data: TimeEntriesRequest
  ): Promise<TimeEntriesResponse> => {
    const response = await api.post("/dashboard/time-entries", data);
    return response.data;
  },

  getEmployeeAnalytics: async (data: any): Promise<any> => {
    const response = await api.post("/dashboard/employee-analytics", data);
    return response.data.analytics;
  },

  getSessionScreenTime: async (sessionId: string): Promise<{
    apps: Array<{ appName: string; totalMinutes: number; segments: number }>;
    totalMinutes: number;
    segmentCount: number;
  }> => {
    const response = await api.get(`/dashboard/screen-time/${sessionId}`);
    return response.data.screenTime;
  },

  getAnalyticsDashboard: async (data: {
    userId: string;
    organizationId: string;
  }): Promise<{ success: boolean; data: AnalyticsDashboardData }> => {
    const response = await api.post("/analytics-dashboard", data);
    return response.data;
  },

  getAnalyticsAIInsights: async (data: {
    userId: string;
    organizationId: string;
    targetUserId: string;
    period: string;
  }): Promise<{
    success: boolean;
    insights: AnalyticsAIInsights | null;
    message?: string;
  }> => {
    const response = await api.post("/analytics-dashboard/ai-insights", data, {
      timeout: 60000,
    });
    return response.data;
  },

  previewInsights: async (data: {
    userId: string;
    organizationId: string;
    targetUserId: string;
    password: string;
    period: string;
  }): Promise<{
    success: boolean;
    insights: import("../types/api").AnalyticsAIInsights | null;
    message?: string;
  }> => {
    const response = await api.post("/analytics-dashboard/ai-insights/preview", data, {
      timeout: 90000,
    });
    return response.data;
  },

  getAnalyticsScreenTime: async (data: {
    userId: string;
    organizationId: string;
    targetUserId: string;
    period: string;
  }): Promise<{
    success: boolean;
    screenTime: {
      apps: { appName: string; totalMinutes: number }[];
      totalMinutes: number;
    };
  }> => {
    const response = await api.post("/analytics-dashboard/screen-time", data);
    return response.data;
  },
};

// Screenshot API
export const screenshotAPI = {
  getSessionScreenshots: async (sessionId: string): Promise<Screenshot[]> => {
    const response = await api.get(`/screenshots/session/${sessionId}`);
    return response.data.screenshots;
  },
  uploadScreenshot: async (data: {
    sessionId: string;
    imageBase64: string;
    capturedAt: string;
    fileSize: number;
  }): Promise<{ success: boolean; screenshotId?: string }> => {
    const response = await api.post("/screenshots/upload", data);
    return response.data;
  },
};

// Users API
export const usersAPI = {
  getUsers: async (organizationId?: string, includeInactive?: boolean): Promise<GetUsersResponse> => {
    const params: Record<string, string> = {};
    if (organizationId) params.organizationId = organizationId;
    if (includeInactive) params.includeInactive = "true";
    const response = await api.get("/users", { params });
    return response.data;
  },

  getUser: async (userId: string): Promise<GetUserResponse> => {
    const response = await api.get(`/users/${userId}`);
    return response.data;
  },

  createUser: async (data: CreateUserRequest): Promise<CreateUserResponse> => {
    const response = await api.post("/users", data);
    return response.data;
  },

  updateUser: async (
    userId: string,
    data: UpdateUserRequest
  ): Promise<GetUserResponse> => {
    const response = await api.put(`/users/${userId}`, data);
    return response.data;
  },

  deleteUser: async (
    userId: string
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
  },

  resetPassword: async (userId: string): Promise<ResetPasswordResponse> => {
    const response = await api.post(`/users/${userId}/reset-password`);
    return response.data;
  },
};

// Organizations API
export const organizationsAPI = {
  getAll: async (): Promise<{
    success: boolean;
    organizations: OrganizationWithStats[];
  }> => {
    const response = await api.get("/organizations");
    return response.data;
  },

  getById: async (
    orgId: string
  ): Promise<{ success: boolean; organization: OrganizationWithStats }> => {
    const response = await api.get(`/organizations/${orgId}`);
    return response.data;
  },

  update: async (
    orgId: string,
    updates: UpdateOrganizationRequest
  ): Promise<{ success: boolean; organization: Organization }> => {
    const response = await api.put(`/organizations/${orgId}`, updates);
    return response.data;
  },

  delete: async (orgId: string): Promise<DeleteOrganizationResponse> => {
    const response = await api.delete(`/organizations/${orgId}`);
    return response.data;
  },

  updateSettings: async (
    orgId: string,
    settings: UpdateOrganizationSettingsRequest
  ): Promise<{ success: boolean; organization: Organization }> => {
    const response = await api.put(
      `/organizations/${orgId}/settings`,
      settings
    );
    return response.data;
  },

  getSystemSettings: async (): Promise<{
    success: boolean;
    settings: { requiredDesktopVersion: string | null; maintenanceMode: boolean };
  }> => {
    const response = await api.get("/organizations/system-settings");
    return response.data;
  },

  updateSystemSettings: async (updates: {
    requiredDesktopVersion?: string | null;
  }): Promise<{ success: boolean }> => {
    const response = await api.patch(
      "/organizations/system-settings",
      updates
    );
    return response.data;
  },

  listRequests: async (
    status?: OrgRequestStatus | "all"
  ): Promise<{ success: boolean; requests: OrganizationRequest[] }> => {
    const response = await api.get("/organizations/requests", {
      params: status && status !== "all" ? { status } : {},
    });
    return response.data;
  },

  approveRequest: async (
    id: string
  ): Promise<{ success: boolean; request?: OrganizationRequest; error?: string; field?: string }> => {
    const response = await api.post(`/organizations/requests/${id}/approve`);
    return response.data;
  },

  rejectRequest: async (
    id: string
  ): Promise<{ success: boolean; request?: OrganizationRequest; error?: string }> => {
    const response = await api.post(`/organizations/requests/${id}/reject`);
    return response.data;
  },
};

// Activity API
export const activityAPI = {
  sendHeartbeat: async (data: {
    sessionId: string;
    deviceType: "desktop" | "mobile" | "tablet";
    isClosing?: boolean;
    eventCount?: number;
    windowFocused?: boolean;
    tabVisible?: boolean;
    activeApp?: string;
    windowTitle?: string;
    source?: "browser" | "electron";
  }): Promise<{
    success: boolean;
    heartbeat: { timestamp: string; isActive: boolean };
    todayActiveMinutes: number;
    sessionActiveMinutes: number;
    isActive: boolean;
    isIdle: boolean;
  }> => {
    const response = await api.post("/activity/heartbeat", data);
    return response.data;
  },

  markIdle: async (data: {
    sessionId: string;
    deviceType: "desktop" | "mobile" | "tablet";
  }): Promise<{ success: boolean }> => {
    const response = await api.post("/activity/idle", data);
    return response.data;
  },

  resumeActivity: async (data: {
    sessionId: string;
    deviceType: "desktop" | "mobile" | "tablet";
  }): Promise<{ success: boolean }> => {
    const response = await api.post("/activity/resume", data);
    return response.data;
  },

  getStatus: async (): Promise<{
    success: boolean;
    status: {
      isActive: boolean;
      isIdle: boolean;
      currentSessionId: string;
      todayActiveMinutes: number;
      currentSessionActiveMinutes: number;
      lastActivity: string | null;
    };
  }> => {
    const response = await api.get("/activity/status");
    return response.data;
  },

  getReport: async (
    date?: string,
    userId?: string
  ): Promise<{
    success: boolean;
    report: any;
  }> => {
    const params = new URLSearchParams();
    if (date) params.append("date", date);
    if (userId) params.append("userId", userId);

    const response = await api.get(`/activity/report?${params}`);
    return response.data;
  },

  getSummary: async (
    userId?: string
  ): Promise<{
    success: boolean;
    summary: any;
  }> => {
    const params = new URLSearchParams();
    if (userId) params.append("userId", userId);

    const response = await api.get(`/activity/summary?${params}`);
    return response.data;
  },

  // Session Snapshot - Save current session metrics to backend (server-side source of truth)
  sendSessionSnapshot: async (data: {
    sessionId: string;
    totalSessionSeconds: number;
    totalIdleSeconds: number;
    totalOfflineSeconds: number;
    currentIdleStartTime: string | null;
    currentOfflineStartTime: string | null;
    source:
      | "unload"
      | "periodic"
      | "transition"
      | "logout"
      | "manual"
      | "reconciliation";
    reconciliation?: {
      gapSeconds: number;
      lastTickAt: string;
      signalsFired: Array<
        "beforeunload" | "network-offline" | "network-online"
      >;
    };
  }): Promise<{
    success: boolean;
    snapshot: {
      id: string;
      timestamp: string;
      source: string;
      totalSessionSeconds?: number;
      totalIdleSeconds?: number;
      totalOfflineSeconds?: number;
    };
  }> => {
    const response = await api.post("/activity/session-snapshot", data);
    return response.data;
  },

  // Browser network-state signal — informs server when window detects
  // online/offline so attribution can distinguish network drops from tab close.
  postNetworkState: async (payload: {
    state: "offline" | "online";
    sessionId: string;
  }): Promise<{ success: boolean }> => {
    const response = await api.post("/activity/network-state", payload);
    return response.data;
  },

  // Get user session snapshots (for displaying session history)
  getSessionSnapshots: async (): Promise<{
    success: boolean;
    snapshots: Array<{
      id: string;
      sessionId: string;
      totalSessionSeconds: number;
      totalIdleSeconds: number;
      totalOfflineSeconds: number;
      source: string;
      timestamp: string;
      createdAt: string;
    }>;
    count: number;
  }> => {
    const response = await api.get("/activity/session-snapshots");
    return response.data;
  },
};

// Billing API
export const billingAPI = {
  create: async (data: CreateBillingEntryRequest): Promise<{ success: boolean; entry: BillingEntry }> => {
    const response = await api.post("/billing", data);
    return response.data;
  },

  getEntries: async (startDate?: string, endDate?: string, noCache?: boolean): Promise<{ success: boolean; entries: BillingEntry[] }> => {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (noCache) params._t = Date.now();
    const response = await api.get("/billing", { params });
    return response.data;
  },

  update: async (id: string, data: UpdateBillingEntryRequest): Promise<{ success: boolean; entry: BillingEntry }> => {
    const response = await api.put(`/billing/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/billing/${id}`);
    return response.data;
  },

  getUserEntries: async (userId: string, startDate?: string, endDate?: string): Promise<{ success: boolean; entries: BillingEntry[] }> => {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await api.get(`/billing/user/${userId}`, { params });
    return response.data;
  },

  getOrgEntries: async (startDate?: string, endDate?: string, userId?: string): Promise<{ success: boolean; entries: BillingEntry[] }> => {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (userId) params.userId = userId;
    const response = await api.get("/billing/org", { params });
    return response.data;
  },
};

export const billingSettingsAPI = {
  getTaskCodes: async (): Promise<{ success: boolean; taskCodes: BillingTaskCode[] }> => {
    const response = await api.get("/billing-settings/task-codes");
    return response.data;
  },

  createTaskCode: async (name: string): Promise<{ success: boolean; taskCode: BillingTaskCode }> => {
    const response = await api.post("/billing-settings/task-codes", { name });
    return response.data;
  },

  updateTaskCode: async (id: string, data: { name?: string; isActive?: boolean }): Promise<{ success: boolean; taskCode: BillingTaskCode }> => {
    const response = await api.put(`/billing-settings/task-codes/${id}`, data);
    return response.data;
  },

  deleteTaskCode: async (id: string): Promise<{ success: boolean; message: string; action: string }> => {
    const response = await api.delete(`/billing-settings/task-codes/${id}`);
    return response.data;
  },

  checkTaskCodeUsage: async (id: string): Promise<{ success: boolean; inUse: boolean }> => {
    const response = await api.get(`/billing-settings/task-codes/${id}/usage`);
    return response.data;
  },

  getOrgNames: async (): Promise<{ success: boolean; orgNames: BillingOrgName[] }> => {
    const response = await api.get("/billing-settings/org-names");
    return response.data;
  },

  createOrgName: async (name: string): Promise<{ success: boolean; orgName: BillingOrgName }> => {
    const response = await api.post("/billing-settings/org-names", { name });
    return response.data;
  },

  updateOrgName: async (id: string, data: { name?: string; isActive?: boolean }): Promise<{ success: boolean; orgName: BillingOrgName }> => {
    const response = await api.put(`/billing-settings/org-names/${id}`, data);
    return response.data;
  },

  deleteOrgName: async (id: string): Promise<{ success: boolean; message: string; action: string }> => {
    const response = await api.delete(`/billing-settings/org-names/${id}`);
    return response.data;
  },

  checkOrgNameUsage: async (id: string): Promise<{ success: boolean; inUse: boolean }> => {
    const response = await api.get(`/billing-settings/org-names/${id}/usage`);
    return response.data;
  },
};

export const branchesAPI = {
  getBranches: async (
    organizationId?: string
  ): Promise<{ success: boolean; branches: OrgBranch[] }> => {
    const params = organizationId ? `?organizationId=${organizationId}` : "";
    const response = await api.get(`/branches${params}`);
    return response.data;
  },

  createBranch: async (data: {
    name: string;
    address: string;
    latitude: number;
    longitude: number;
    organizationId: string;
  }): Promise<{ success: boolean; branch: OrgBranch }> => {
    const response = await api.post("/branches", data);
    return response.data;
  },

  updateBranch: async (
    id: string,
    data: {
      name?: string;
      address?: string;
      latitude?: number;
      longitude?: number;
      isActive?: boolean;
    }
  ): Promise<{ success: boolean; branch: OrgBranch }> => {
    const response = await api.put(`/branches/${id}`, data);
    return response.data;
  },

  deleteBranch: async (
    id: string
  ): Promise<{ success: boolean; message: string; action: string }> => {
    const response = await api.delete(`/branches/${id}`);
    return response.data;
  },

  checkBranchUsage: async (
    id: string
  ): Promise<{ success: boolean; inUse: boolean }> => {
    const response = await api.get(`/branches/${id}/usage`);
    return response.data;
  },
};

// Profile API (self-service)
export const profileAPI = {
  get: async (): Promise<{ success: boolean; user: User }> => {
    const response = await api.get("/profile");
    return response.data;
  },

  update: async (data: {
    currentPassword: string;
    name?: string;
    email?: string;
    newPassword?: string;
  }): Promise<{ success: boolean; user: User }> => {
    const response = await api.put("/profile", data);
    return response.data;
  },
};

// Internal axios instance exposed so sibling client files (e.g.,
// subscriptionsApi) can share interceptors and base config without
// duplicating axios setup.
export const apiClient = api;

// Unified API service for components
export const apiService = {
  // System Owner functions
  createOrganization: authAPI.createOrganization,
  getOrganizations: authAPI.getOrganizations,

  // Auth functions
  login: authAPI.login,
  initializeSystem: authAPI.initializeSystem,
  getSystemStatus: authAPI.getSystemStatus,
  me: authAPI.me,
  logout: authAPI.logout,
  getDesktopAuthCode: authAPI.getDesktopAuthCode,
  pollDesktopCallback: authAPI.pollDesktopCallback,
  forgotPassword: authAPI.forgotPassword,
  verifyPasswordResetToken: authAPI.verifyPasswordResetToken,
  submitPasswordReset: authAPI.submitPasswordReset,

  // Time tracking functions
  clockIn: timeAPI.clockIn,
  clockOut: timeAPI.clockOut,
  getTimeEntries: timeAPI.getEntries,
  getTimeStatus: timeAPI.getStatus,

  // Status functions
  createStatus: statusAPI.create,
  updateStatus: statusAPI.update,
  deleteStatus: statusAPI.delete,
  getStatusEntries: statusAPI.getEntries,

  // Dashboard functions
  getDashboard: dashboardAPI.getOverview,
  getCalendar: dashboardAPI.getCalendar,
  getUsers: dashboardAPI.getUsers,
  // User management functions
  getUsersManagement: usersAPI.getUsers,
  getUser: usersAPI.getUser,
  createUser: usersAPI.createUser,
  updateUser: usersAPI.updateUser,
  deleteUser: usersAPI.deleteUser,
  resetPassword: usersAPI.resetPassword,

  // Organization management functions (system owner only)
  getAllOrganizations: organizationsAPI.getAll,
  getOrganization: organizationsAPI.getById,
  updateOrganization: organizationsAPI.update,
  deleteOrganization: organizationsAPI.delete,
  updateOrganizationSettings: organizationsAPI.updateSettings,
  getSystemSettings: organizationsAPI.getSystemSettings,
  updateSystemSettings: organizationsAPI.updateSystemSettings,

  // Activity tracking functions
  sendActivityHeartbeat: activityAPI.sendHeartbeat,
  markUserIdle: activityAPI.markIdle,
  resumeUserActivity: activityAPI.resumeActivity,
  getActivityStatus: activityAPI.getStatus,
  getActivityReport: activityAPI.getReport,
  getActivitySummary: activityAPI.getSummary,
  sendSessionSnapshot: activityAPI.sendSessionSnapshot,
  postNetworkState: activityAPI.postNetworkState,

  // Billing functions
  createBillingEntry: billingAPI.create,
  getBillingEntries: billingAPI.getEntries,
  updateBillingEntry: billingAPI.update,
  deleteBillingEntry: billingAPI.delete,
  getUserBillingEntries: billingAPI.getUserEntries,

  // Branch functions
  getBranches: branchesAPI.getBranches,
  createBranch: branchesAPI.createBranch,
  updateBranch: branchesAPI.updateBranch,
  deleteBranch: branchesAPI.deleteBranch,
  checkBranchUsage: branchesAPI.checkBranchUsage,

  // Profile functions (self-service)
  getProfile: profileAPI.get,
  updateProfile: profileAPI.update,

  // Generic helpers
  get: async (url: string, config?: any) => {
    const response = await api.get(url, config);
    return response.data;
  },
  post: async (url: string, data?: any, config?: any) => {
    const response = await api.post(url, data, config);
    return response.data;
  },
  put: async (url: string, data?: any, config?: any) => {
    const response = await api.put(url, data, config);
    return response.data;
  },
};

export default api;
